import { useState } from "react";
import { useTranslation } from "react-i18next";
import { sendAssistantFeedback } from "../lib/api";

export const FEEDBACK_OPTIONS = ["useful", "not_useful", "correct", "needs_review"] as const;
export type Feedback = (typeof FEEDBACK_OPTIONS)[number];

/**
 * Useful / Not useful / Correct / Needs review on an AI output (requirements 9.6 and 9.10): one vote per interaction,
 * stored with the interaction in the AI module so demo acceptance can be counted. Renders nothing without an id.
 */
export function FeedbackControl({ interactionId, className = "", size = "xs", onRecorded }: {
  interactionId: string | null | undefined; className?: string; size?: "xs" | "xxs"; onRecorded?: (f: Feedback) => void;
}) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<Feedback | null>(null);
  const [busy, setBusy] = useState(false);
  if (!interactionId) return null;
  const vote = async (f: Feedback) => {
    setBusy(true);
    try {
      if (await sendAssistantFeedback(interactionId, f)) { setChosen(f); onRecorded?.(f); }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`flex flex-wrap items-center gap-2 text-brand-grey ${size === "xs" ? "text-xs" : "text-[11px]"} ${className}`} data-feedback={interactionId}>
      <span>{chosen ? t("feedback.recorded") : t("feedback.question")}</span>
      {FEEDBACK_OPTIONS.map((f) => (
        <button key={f} type="button" disabled={busy || chosen !== null} data-feedback-option={f} onClick={() => void vote(f)}
          className={`rounded border px-2 py-0.5 ${chosen === f ? "border-brand-primary bg-blue-50 text-brand-primary" : chosen ? "border-brand-grey-light opacity-50" : "border-brand-grey-light hover:bg-brand-grey-light/40"}`}>
          {t(`feedback.${f}`)}
        </button>
      ))}
    </div>
  );
}
